<script lang="ts" setup>
import { getDisplayDateTime, isImage } from "@/modules/common";
import {
  Image,
  ImagePreview,
  ImagePreviewInstance,
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { computed, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useMedia } from "@/stores/media";
import { useI18n } from "vue-i18n";
import { MediaWithMateMediaId } from "@/types/entity";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { useRouter } from "vue-router";
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    show: boolean;
    mateMediaList: MediaWithMateMediaId[];
    startPosition: number;
    enableDelete?: boolean;
    showMediaInfo?: boolean;
    mediaModule?: "mate" | "module";
  }>(),
  {
    enableDelete: false,
    showMediaInfo: true,
    mediaModule: "mate",
  }
);
const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "afterDelete", mediaId: number): void;
}>();
const router = useRouter();

const previewUrls = computed(() => {
  return props.mateMediaList.map((item) => item.url);
});
const previewIdx = ref(0);
const previewRef = ref<HTMLElement | null>(null);
const imagePreviewRef = ref<ImagePreviewInstance | null>(null);
onClickOutside(previewRef, (e: PointerEvent) => {
  const classList = (e.target as HTMLElement).classList;
  if (classList.contains("van-image-preview__close-icon")) {
    emit("update:show", false);
  }
});

// EventHandler
const onPreviewIdxChange = (idx: number) => {
  previewIdx.value = idx;
};
const media = useMedia();
const deleteMateMedia = async ({
  mateMediaId,
  moduleMediaId,
  id,
}: MediaWithMateMediaId) => {
  try {
    await showConfirmDialog({
      title: t("ConfirmModal.sureDeleteText"),
      message: t("ConfirmModal.deleteCurrentChatInfo"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
    const loadingToast = showLoadingToast({
      message: t("Message.deleting"),
      duration: 0,
    });
    try {
      if (props.mediaModule === "mate") {
        await media.deleteMateMedia(mateMediaId!);
      } else {
        await media.deleteModuleMedia(moduleMediaId!);
      }
      const isFirst = props.mateMediaList.length === 1;
      const isLast = previewIdx.value === props.mateMediaList.length - 1;
      emit("afterDelete", id!);
      if (isFirst && isLast) {
        emit("update:show", false);
      } else if (isLast) {
        previewIdx.value = previewIdx.value - 1;
        imagePreviewRef.value?.swipeTo(previewIdx.value);
      }
      loadingToast.close();
      showSuccessToast(t("Message.deleteSuccessful"));
    } finally {
      loadingToast.close();
    }
  } catch {
    return;
  }
};

// setting
// const showSetting = ref(false);
// const clickSetting = (media: any) => {
//   showSetting.value = true;
// };
const videoRef = ref<HTMLVideoElement | null>(null);
const onClose = () => {
  emit("update:show", false);
  if (videoRef.value) {
    videoRef.value.pause();
    videoRef.value.currentTime = 0;
  } else {
    console.log("videoRef.value is null");
  }
};

const clickToEdit = () => {
  sessionStorageSet("mate-wish-edit", {
    content: "",
    referenceImagezz: [
      {
        id: props.mateMediaList[previewIdx.value].id,
        url: props.mateMediaList[previewIdx.value].url,
      },
    ],
  });

  router.push({
    name: "mateWishEdit",
  });
};
</script>
<template>
  <image-preview
    ref="imagePreview"
    :before-close="() => false"
    :images="previewUrls"
    :show="show"
    :start-position="startPosition"
    class="media-preview"
    closeable
    teleport="body"
    @close="onClose"
    @change="onPreviewIdxChange"
    @update:show="emit('update:show', $event)"
  >
    <!--  Vant有Bug，在调用swipeTo切换idx后，index不会更新，所以需要自己用slot实现  -->
    <template #index> {{ previewIdx + 1 }} / {{ previewUrls.length }}</template>
    <template #image="{ src }">
      <div ref="previewRef" class="max-w-xl my-0 mx-auto">
        <Image
          v-if="isImage(src)"
          :src="src"
          fit="scale-down"
          crossorigin="anonymous"
        />
        <div v-else>
          <video
            ref="videoRef"
            :poster="mateMediaList[previewIdx].videoCoverUrl"
            controls
            style="width: 100%"
            crossorigin="anonymous"
          >
            <source :src="src" />
          </video>
        </div>
      </div>
    </template>
    <template #cover>
      <div v-if="showMediaInfo" class="bg-base-100 shadow-2xl p-4">
        <div class="flex justify-between items-center">
          <div class="text-xs text-base-content/50">
            {{ getDisplayDateTime(mateMediaList[previewIdx]?.dtCreate!) }}
          </div>
          <div class="flex gap-1 items-center">
            <button class="btn btn-sm rounded" @click="clickToEdit">
              改图
            </button>

            <button
              v-if="
                enableDelete &&
                ((mediaModule === 'mate' &&
                  mateMediaList[previewIdx]?.mateMediaId) ||
                  mediaModule === 'module')
              "
              class="btn btn-sm rounded"
              @click="deleteMateMedia(mateMediaList[previewIdx])"
            >
              <i class="iconfont icon-delete text-sm"></i>
            </button>
            <!-- <button
              class="btn btn-sm rounded"
              @click="clickSetting(mateMediaList[previewIdx])"
            >
              <i class="iconfont icon-a-1 text-sm"></i>
            </button> -->
            <!-- <button class="btn btn-xs rounded">
              <a :href="mateMediaList[previewIdx].url" download>
                <i class="iconfont icon-download text-sm"></i>
              </a>
            </button> -->
          </div>
        </div>
      </div>

      <!-- <PaidSetting
        :show="showSetting"
        :mate-media="mateMediaList[previewIdx]"
      /> -->
    </template>
  </image-preview>
</template>
<style>
.van-image-preview.media-preview {
  margin-top: env(safe-area-inset-top, 0);
}

.media-preview .van-image-preview__cover {
  top: unset;
  bottom: 0;
  left: 0;
  right: 0;
}

.media-preview .van-image-preview__image-wrap {
  text-align: center;
}
</style>
